import { type ColumnDef } from '@tanstack/react-table';
import { filesize } from 'filesize';
import { ColumnType } from '@/types/column';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface SizeColumnOptions<T> {
  id?: string;
  accessorKey: keyof T;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  label: string;
  galleryView: GalleryView;
  size?: number;
  cellClassName?: string;
  headerClassName?: string;
  dataCy?: string;
}

export const SizeColumn = <T extends Record<string, any>>(opts: SizeColumnOptions<T>): ColumnDef<T> => {
  const {
    id = ColumnType.Size,
    accessorKey = 'size',
    label = 'Size',
    galleryView,
    size = 90,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    minSize = 30,
    maxSize = 120,
    dataCy,
  } = opts;

  return {
    id,
    accessorKey,
    enableSorting,
    enableHiding,
    minSize,
    maxSize,
    ...((cellClassName ?? headerClassName) && {
      meta: {
        ...(cellClassName && { cellClassName: cellClassName }),
        ...(headerClassName && { headerClassName: headerClassName }),
      },
    }),
    size,
    header: () => label,
    cell: ({ getValue, row }) => {
      const raw = getValue();
      const num = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;

      if (!Number.isFinite(num) || num < 0) {
        return '';
      }

      return (
        <p
          className={cn('whitespace-nowrap text-text-gray', {
            'text-xs': galleryView === GalleryView.TILE,
          })}
          data-cy={dataCy ?? `${galleryView ? galleryView : 'list'}-${row.index}-${id}`}
        >
          {filesize(getValue() as number)}
        </p>
      );
    },
  };
};
